import { useCallback, useRef, useState } from 'react'

export interface Toast {
  id: string
  message: string
  kind?: 'ok' | 'err' | ''
  count?: number
}

const MAX_VISIBLE = 3
const DEDUPE_MS = 8000

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([])
  const recentRef = useRef<Map<string, number>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  const push = useCallback((message: string, kind: Toast['kind'] = '') => {
    const text = String(message || '').trim()
    if (!text) return

    const key = `${kind}|${text}`
    const now = Date.now()
    const last = recentRef.current.get(key) || 0
    if (now - last < DEDUPE_MS) {
      setToasts((t) => {
        const idx = t.findIndex((x) => x.message === text && x.kind === kind)
        if (idx < 0) return t
        const next = [...t]
        const cur = next[idx]
        next[idx] = { ...cur, count: (cur.count || 1) + 1 }
        return next
      })
      return
    }
    recentRef.current.set(key, now)

    const id = crypto.randomUUID()
    setToasts((t) => {
      const next = [...t, { id, message: text, kind, count: 1 }]
      return next.slice(-MAX_VISIBLE)
    })
    window.setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id))
    }, 4200)
  }, [])

  const clear = useCallback(() => setToasts([]), [])

  return { toasts, push, dismiss, clear }
}
